import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import axiosInstance from 'utils/axiosUtil';
import { STARK_CATEGORIES, isStarkEntryLocked } from './starkConstants';

const styles = (theme) => ({
  field: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  splitToggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  allocationTable: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.75, 1),
    },
  },
  providerChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  warningAlert: {
    marginBottom: theme.spacing(1),
  },
  lockedBanner: {
    marginBottom: theme.spacing(2),
  },
  amountInput: {
    width: '100%',
  },
  noProviders: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: 13,
    padding: theme.spacing(1, 0),
  },
});

const EMPTY_FORM = {
  category:      '',
  description:   '',
  total_amount:  '',
  spending_date: null,
  custom_split:  false,
  // provider_ids comes from props.providers, but user can add/remove
};

/**
 * Even-split utility — mirrors backend _buildAllocations.
 * First provider absorbs rounding remainder.
 */
function evenSplit(totalAmount, providerIds) {
  const count = providerIds.length;
  if (count === 0) return [];
  const total_c   = Math.round(parseFloat(totalAmount) * 100);
  const base      = Math.floor(total_c / count);
  const remainder = total_c - base * count;
  return providerIds.map((pid, idx) => ({
    provider_id: pid,
    amount:      ((base + (idx === 0 ? remainder : 0)) / 100).toFixed(2),
  }));
}

class StarkDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      form:          { ...EMPTY_FORM },
      // Mutable list of provider ids for this entry (pre-populated from note providers)
      providerIds:   [],
      // [{provider_id, amount}] — null means use auto even-split
      customAmounts: {},
      saving:        false,
      saveError:     null,
      // Warnings returned by the server after save/preview
      serverWarnings: [],
    };
  }

  componentDidMount() {
    this._resetForm();
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this._resetForm();
    }
  }

  _resetForm() {
    const { log, providers, initialDescription } = this.props;
    this.setState({ saveError: null, saving: false, serverWarnings: [] });

    if (log) {
      // Editing an existing entry
      const existingProviderIds = (log.allocations || []).map((a) => a.provider_id);
      const customAmounts = {};
      (log.allocations || []).forEach((a) => {
        customAmounts[a.provider_id] = String(a.allocated_amount);
      });

      // Detect whether the original entry used a custom split by checking if
      // any allocation deviates from an even split. Preserving this prevents
      // the edit dialog from silently resetting custom amounts to even splits.
      const total = parseFloat(log.total_amount) || 0;
      const count = existingProviderIds.length;
      const evenShare = count > 0 ? total / count : 0;
      const wasCustomSplit = count > 0 && (log.allocations || []).some(
        (a) => Math.abs(parseFloat(a.allocated_amount) - evenShare) > 0.02
      );

      this.setState({
        form: {
          category:      log.category     || '',
          description:   log.description  || '',
          total_amount:  log.total_amount  != null ? String(log.total_amount) : '',
          spending_date: log.spending_date ? new Date(log.spending_date) : null,
          custom_split:  wasCustomSplit,
        },
        providerIds:   existingProviderIds,
        customAmounts,
      });
    } else {
      // New entry — pre-populate providers from the note
      const initialProviderIds = (providers || []).map((p) => p.id);
      this.setState({
        form: {
          ...EMPTY_FORM,
          spending_date: new Date(),
          description:   initialDescription || '',
        },
        providerIds:   initialProviderIds,
        customAmounts: {},
      });
    }
  }

  onChange = (field, value) => {
    this.setState((prev) => ({
      form: { ...prev.form, [field]: value },
      // Clear server warnings when user changes the form
      serverWarnings: [],
    }));
  };

  onCustomAmountChange = (providerId, value) => {
    this.setState((prev) => ({
      customAmounts: { ...prev.customAmounts, [providerId]: value },
    }));
  };

  toggleCustomSplit = () => {
    this.setState((prev) => {
      const turning_on = !prev.form.custom_split;
      // Pre-populate custom amounts from current even split when toggling on
      let customAmounts = prev.customAmounts;
      if (turning_on) {
        const total  = parseFloat(prev.form.total_amount) || 0;
        const splits = evenSplit(total, prev.providerIds);
        customAmounts = {};
        splits.forEach((s) => { customAmounts[s.provider_id] = s.amount; });
      }
      return {
        form: { ...prev.form, custom_split: turning_on },
        customAmounts,
      };
    });
  };

  _getAllocations() {
    const { form, providerIds, customAmounts } = this.state;
    const total = parseFloat(form.total_amount) || 0;

    if (form.custom_split) {
      return providerIds.map((pid) => ({
        provider_id: pid,
        amount:      parseFloat(customAmounts[pid] || 0).toFixed(2),
      }));
    }
    return evenSplit(total, providerIds);
  }

  _getProviderName(providerId) {
    const { providers } = this.props;
    const p = (providers || []).find((p) => p.id === providerId);
    if (p) return `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Provider #${providerId}`;

    // May be in allocations (edit mode)
    const { log } = this.props;
    if (log) {
      const alloc = (log.allocations || []).find((a) => a.provider_id === providerId);
      if (alloc?.provider) {
        return `${alloc.provider.first_name || ''} ${alloc.provider.last_name || ''}`.trim();
      }
    }
    return `Provider #${providerId}`;
  }

  canSave() {
    const { form, providerIds } = this.state;
    if (!form.category) return false;
    const amt = parseFloat(form.total_amount);
    if (isNaN(amt) || amt <= 0) return false;
    if (!form.spending_date) return false;
    if (providerIds.length === 0) return false;
    if (form.custom_split) {
      // All custom amounts must be positive numbers summing to total
      const sum = providerIds.reduce(
        (s, pid) => s + (parseFloat(this.state.customAmounts[pid]) || 0),
        0,
      );
      if (Math.abs(sum - amt) > 0.02) return false;
    }
    return true;
  }

  onSave = () => {
    const { clientId, log, activityNoteId, onSaved } = this.props;
    const { form, providerIds } = this.state;

    this.setState({ saving: true, saveError: null, serverWarnings: [] });

    const spendingDate = form.spending_date instanceof Date
      ? form.spending_date.toISOString().split('T')[0]
      : form.spending_date;

    const allocations = this._getAllocations();

    const payload = {
      category:      Number(form.category),
      description:   form.description || null,
      total_amount:  parseFloat(form.total_amount),
      spending_date: spendingDate,
      provider_ids:  providerIds,
      activity_note_id: activityNoteId || null,
    };

    if (form.custom_split) {
      payload.custom_allocations = allocations.map((a) => ({
        provider_id: a.provider_id,
        amount:      parseFloat(a.amount),
      }));
    }

    const request = log
      ? axiosInstance.put(`/v2/stark/logs/${log.id}`, payload)
      : axiosInstance.post('/v2/stark/logs', payload, { params: { clientId } });

    request.then((response) => {
      const data     = response?.data || {};
      // Server returns { log, warnings } for create; { log, warnings } for update too
      const saved    = data.log || data;
      const warnings = data.warnings || [];

      if (warnings.length > 0) {
        // Surface warnings but still call onSaved — warnings are non-blocking
        this.setState({ saving: false, serverWarnings: warnings });
      } else {
        this.setState({ saving: false });
      }

      if (onSaved) onSaved(saved, warnings);
    }).catch((err) => {
      console.error('StarkDialog save failed', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'An unexpected error occurred. Please try again.';
      this.setState({ saving: false, saveError: message });
    });
  };

  onDelete = () => {
    const { log, onDeleted } = this.props;
    if (!log) return;

    this.setState({ saving: true, saveError: null });
    axiosInstance.delete(`/v2/stark/logs/${log.id}`)
      .then(() => {
        this.setState({ saving: false });
        if (onDeleted) onDeleted(log.id);
      })
      .catch((err) => {
        console.error('StarkDialog delete failed', err);
        const message = err?.response?.data?.error || err?.message || 'Delete failed.';
        this.setState({ saving: false, saveError: message });
      });
  };

  render() {
    const { open, onClose, log, activityNoteId, fromActivityNote, providers, classes } = this.props;
    const { form, providerIds, customAmounts, saving, saveError, serverWarnings } = this.state;

    const isEdit = Boolean(log);

    // New entries must always be created from an Activity Note.
    // fromActivityNote allows new notes (no id yet) to pass through.
    if (!isEdit && !activityNoteId && !fromActivityNote) {
      return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>Log Non-Monetary Compensation</DialogTitle>
          <DialogContent dividers>
            <Typography>
              Non-monetary compensation entries must be created from an Activity Note. Please open
              or create an Activity Note first, then log your entry from there.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Dialog>
      );
    }
    const locked = isEdit && log.spending_date && isStarkEntryLocked(log.spending_date);
    const allocations = this._getAllocations();
    const totalAmount = parseFloat(form.total_amount) || 0;

    // Validate custom split sum
    let customSplitError = null;
    if (form.custom_split && providerIds.length > 0) {
      const sum = providerIds.reduce(
        (s, pid) => s + (parseFloat(customAmounts[pid]) || 0),
        0,
      );
      if (Math.abs(sum - totalAmount) > 0.02) {
        customSplitError = `Allocations total $${sum.toFixed(2)} — must equal $${totalAmount.toFixed(2)}.`;
      }
    }

    const warningIcons = {
      annual_exceeded:   <ErrorOutlineIcon fontSize="small" color="error" />,
      annual_warning:    <WarningAmberIcon fontSize="small" color="warning" />,
      per_occurrence_meal: <InfoOutlinedIcon fontSize="small" color="info" />,
    };

    return (
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? 'Edit Stark Entry' : 'Log Non-Monetary Compensation'}
        </DialogTitle>

        <DialogContent dividers>
          {locked && (
            <Alert severity="warning" className={classes.lockedBanner}>
              This entry is from a prior year and is past the Jan 30 edit window. It cannot be modified.
            </Alert>
          )}

          {saveError && (
            <Alert severity="error" className={classes.field}>
              {saveError}
            </Alert>
          )}

          {serverWarnings.map((w, i) => (
            <Alert
              key={i}
              severity={w.type === 'annual_exceeded' ? 'error' : w.type === 'annual_warning' ? 'warning' : 'info'}
              icon={warningIcons[w.type] || undefined}
              className={classes.warningAlert}
            >
              {w.message}
            </Alert>
          ))}

          {/* Category + Date */}
          <Box className={classes.row}>
            <FormControl className={classes.field} required>
              <InputLabel id="stark-category-label">Category</InputLabel>
              <Select
                labelId="stark-category-label"
                value={form.category}
                label="Category"
                onChange={(e) => this.onChange('category', e.target.value)}
                disabled={locked || saving}
              >
                {STARK_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label="Spending Date"
              value={form.spending_date}
              onChange={(val) => this.onChange('spending_date', val)}
              disabled={locked || saving}
              disableFuture
              slotProps={{
                textField: {
                  className: classes.field,
                  required: true,
                },
              }}
            />
          </Box>

          {/* Total Amount */}
          <TextField
            label="Total Amount"
            value={form.total_amount}
            onChange={(e) => this.onChange('total_amount', e.target.value)}
            disabled={locked || saving}
            required
            type="number"
            inputProps={{ min: 0, step: '0.01', style: { MozAppearance: 'textfield' } }}
            sx={{ '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { display: 'none' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            className={classes.field}
            helperText="Full event cost before splitting across providers."
          />

          {/* Description */}
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => this.onChange('description', e.target.value)}
            disabled={locked || saving}
            multiline
            minRows={2}
            className={classes.field}
            inputProps={{ maxLength: 255 }}
          />

          {/* Providers + Split ─────────────────────────────────────────────── */}
          <Typography className={classes.sectionLabel}>
            Provider Allocations
          </Typography>

          {providerIds.length === 0 ? (
            <Typography className={classes.noProviders}>
              {(!providers || providers.length === 0)
                ? 'Add providers to your Activity Note to log.'
                : 'No providers on this entry.'}
            </Typography>
          ) : (
            <>
              {/* Provider chips */}
              <Box mb={1}>
                {providerIds.map((pid) => (
                  <Chip
                    key={pid}
                    label={this._getProviderName(pid)}
                    size="small"
                    className={classes.providerChip}
                    onDelete={
                      !locked && !saving
                        ? () => this.setState((prev) => ({
                            providerIds: prev.providerIds.filter((id) => id !== pid),
                            customAmounts: { ...prev.customAmounts, [pid]: undefined },
                          }))
                        : undefined
                    }
                  />
                ))}
              </Box>

              {/* Custom split toggle */}
              {!locked && (
                <Box className={classes.splitToggleRow}>
                  <Typography variant="body2" color="text.secondary">
                    Custom split
                    <Tooltip title="By default amounts are split evenly. Toggle to specify a custom amount per provider.">
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ verticalAlign: 'middle', ml: 0.5, opacity: 0.5 }}
                      />
                    </Tooltip>
                  </Typography>
                  <Switch
                    size="small"
                    checked={form.custom_split}
                    onChange={this.toggleCustomSplit}
                    disabled={saving || providerIds.length < 2}
                  />
                </Box>
              )}

              {/* Allocation table */}
              <Table size="small" className={classes.allocationTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providerIds.map((pid) => {
                    const alloc = allocations.find((a) => a.provider_id === pid);
                    const displayAmt = alloc ? alloc.amount : '0.00';
                    return (
                      <TableRow key={pid}>
                        <TableCell>{this._getProviderName(pid)}</TableCell>
                        <TableCell align="right">
                          {form.custom_split && !locked ? (
                            <TextField
                              value={customAmounts[pid] || ''}
                              onChange={(e) => this.onCustomAmountChange(pid, e.target.value)}
                              type="number"
                              inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', width: 80 } }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              size="small"
                              disabled={saving}
                            />
                          ) : (
                            <Typography variant="body2">${displayAmt}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {providerIds.length > 1 && (
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          <em>Total</em>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          ${totalAmount.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {customSplitError && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {customSplitError}
                </FormHelperText>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Box>
            {isEdit && !locked && (
              <Button
                onClick={this.onDelete}
                color="error"
                disabled={saving}
                size="small"
              >
                Delete
              </Button>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={this.onSave}
              variant="contained"
              disabled={saving || locked || !this.canSave()}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Log Entry'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  }
}

StarkDialog.propTypes = {
  open:            PropTypes.bool.isRequired,
  onClose:         PropTypes.func.isRequired,
  onSaved:         PropTypes.func,   // (savedLog, warnings) => void
  onDeleted:       PropTypes.func,   // (id) => void
  clientId:        PropTypes.number, // required for new entries
  activityNoteId:  PropTypes.number,  // link to note (null for new unsaved notes)
  fromActivityNote: PropTypes.bool,   // true when opened from ProviderCommentsDialog
  log:             PropTypes.object, // present when editing
  providers:           PropTypes.array,  // [{id, first_name, last_name}] pre-populated from note
  initialDescription:  PropTypes.string, // pre-filled from the activity note body
  classes:             PropTypes.object.isRequired,
};

StarkDialog.defaultProps = {
  providers: [],
};

export default withStyles(styles)(StarkDialog);
